import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useSetRecoilState, useRecoilState, useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  Modal,
  Spin,
  Tag,
  Divider,
  Table,
  Upload,
  Popover
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { reloadSpecialServicesAtom, notificationsAtom, userPrivilagesAtom } from '../../../../atoms/Atoms';
import Button from '../../../shared-components/buttons/Button';
import PopconfirmButton from '../../../shared-components/buttons/PopconfirmButton';
import AntdInput from '../../../shared-components/form/AntdInput';
import AntdDatePicker from '../../../shared-components/form/AntdDatePicker';
import AntdInputNumber from '../../../shared-components/form/AntdInputNumber';
import Select from '../../../shared-components/form/Select';
import api from '../../../../api/api';
import {
  thousandAllowZero,
  localizeParatransitRouteDates,
  localizeParatransitServiceDates,
  paratransitServiceStatusToColor,
  paratransitServiceStatusToString,
  paratransitServiceRouteStatusToString,
  paratransitServiceRouteStatusToColor,
  thousand,
  uuidv4,
  toLocalTime
} from '../../../../functions';
import AntdTimePicker from '../../../shared-components/form/AntdTimePicker';
import { DraggableBodyRow, DragHandle, paratransitServiceRouteStatus, paratransitServiceStatus } from '../../../../utils';

function SelectedParatransitServicesModal({ onClose, service }) {
  const defaultRoute = {
    clientName: '',
    code: '',
    tripOriginAddress: '',
    coordinatedPickupTime: dayjs().format('MMM DD, YYYY hh:mm A'),
    tripDestinationAddress: '',
    pickupArrivalTime: null,
    destinationArrivalTime: null,
    initialMileage: null,
    finalMileage: null,
    status: 0,
    statusMessage: ''
  };
  const { Dragger } = Upload;
  const { t, i18n } = useTranslation();
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const setReloadSpecialServices = useSetRecoilState(reloadSpecialServicesAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [rejectionModal, setRejectionModal] = useState(false);
  const [rejectionMessage, setRejectionMessage] = useState('');
  const [viewLogs, setViewLogs] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logs, setLogs] = useState([]);

  const draggerProps = {
    name: 'file',
    multiple: false,
    showUploadList: true,
    maxCount: 1,
    fileList: data?.fileData ? [data.fileData] : [],
    beforeUpload: (file) => {
      setData({
        ...data,
        fileData: file
      });
      return false;
    },
    ondrop: (e) => {
    },
    onRemove: (e) => {
        setData({
        ...data,
        fileData: null
      });
    }
  };

  function calculateTripNumber(routes) {
    const tempRoutes = [...routes];
    for (let i = 0; i < tempRoutes.length; i++) {
      tempRoutes[i].tripNumber = i + 1;
    }
    return tempRoutes;
  }

  function valid() {
    let isValid = true;
    let tempData = {
      ...data
    };
  
    if (data?.status >= 1) {
      if (!data?.date) {
        isValid = false;
        tempData = {
          ...tempData,
          dateError: t('date_is_required')
        }
      } else {
        tempData = {
          ...tempData,
          garageDdateErrorepartureTimeError: ''
        }
      }
  
      if (data?.passengers?.length === 0) {
        isValid = false;
        tempData = {
          ...tempData,
          passengersError: t('amount_needs_to_be_greater_than_zero')
        }
      } else {
        const tempNum = parseInt(data?.passengers, 10);
        if (tempNum === 0) {
          isValid = false;
          tempData = {
            ...tempData,
            passengersError: t('amount_needs_to_be_greater_than_zero')
          }
        } else {
          tempData = {
            ...tempData,
            passengersError: ''
          }
        }
      }
  
      let initMil = null;
      let finalMil = null;
      if (data?.initialMileage?.length === 0) {
        isValid = false;
        tempData = {
          ...tempData,
          initialMileageError: t('amount_needs_to_be_greater_than_zero')
        }
      } else {
        const tempNum = parseInt(data?.initialMileage, 10);
        if (tempNum === 0) {
          isValid = false;
          tempData = {
            ...tempData,
            initialMileageError: t('amount_needs_to_be_greater_than_zero')
          }
        } else {
          initMil = tempNum;
          tempData = {
            ...tempData,
            initialMileageError: ''
          }
        }
      }
  
      if (data?.finalMileage?.length === 0) {
        isValid = false;
        tempData = {
          ...tempData,
          finalMileageError: t('amount_needs_to_be_greater_than_zero')
        }
      } else {
        const tempNum = parseInt(data?.finalMileage, 10);
        if (tempNum === 0) {
          isValid = false;
          tempData = {
            ...tempData,
            finalMileageError: t('amount_needs_to_be_greater_than_zero')
          }
        } else {
          finalMil = tempNum;
          tempData = {
            ...tempData,
            finalMileageError: ''
          }
        }
      }
  
      if (initMil !== null && finalMil !== null) {
        if (finalMil < initMil) {
          isValid = false;
          tempData = {
            ...tempData,
            finalMileageError: t('final_mileage_is_less_than_initial')
          }
        }
      }
  
      if (data?.driverName?.length === 0) {
        isValid = false;
        tempData = {
          ...tempData,
          driverNameError: t('field_is_required')
        }
      } else {
        tempData = {
          ...tempData,
          driverNameError: ''
        }
      }
  
      if (data?.unitBusNumber?.length === 0) {
        isValid = false;
        tempData = {
          ...tempData,
          unitBusNumberError: t('field_is_required')
        }
      } else {
        tempData = {
          ...tempData,
          unitBusNumberError: ''
        }
      }
  
      if (data?.supervisorName?.length === 0) {
        isValid = false;
        tempData = {
          ...tempData,
          supervisorNameError: t('field_is_required')
        }
      } else {
        tempData = {
          ...tempData,
          supervisorNameError: ''
        }
      }
  
      if (!data?.garageDepartureTime) {
        isValid = false;
        tempData = {
          ...tempData,
          garageDepartureTimeError: t('time_is_required')
        }
      } else {
        tempData = {
          ...tempData,
          garageDepartureTimeError: ''
        }
      }
  
      if (!data?.garageArrivalTime) {
        isValid = false;
        tempData = {
          ...tempData,
          garageArrivalTimeError: t('time_is_required')
        }
      } else {
        tempData = {
          ...tempData,
          garageArrivalTimeError: ''
        }
      }
  
      if (data?.garageDepartureTime && data?.garageArrivalTime && data?.date) {
        const datePartString = dayjs(data?.date).format('YYYY-MM-DD');
        const startingHour = dayjs(data?.garageDepartureTime).format('HH:mm:ss');
        const endingHour = dayjs(data?.garageArrivalTime).format('HH:mm:ss');
        const startingTime = dayjs(`${datePartString}T${startingHour}`).add(4, 'hour');
        const endingTime = dayjs(`${datePartString}T${endingHour}`).add(4, 'hour');
        if (endingTime.isSame(startingTime) || endingTime.isBefore(startingTime)) {
          isValid = false;
          tempData = {
            ...tempData,
            garageArrivalTimeError: t('time_too_close_to_departure')
          }
        } else {
          tempData = {
            ...tempData,
            garageArrivalTimeError: ''
          }
        }
      }
    }

    setData(tempData);
    return isValid;
  }

  function changeStatus(newStatus) {
    if (!valid()) {
      return;
    }
  
    setLoading(true);
    api.put('sonnell/paratransitservice/status', {
      Id: data?.id,
      Status: newStatus,
      Message: rejectionMessage
    })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setReloadSpecialServices(true);
          setData(localizeParatransitServiceDates(response.data.data));
          if (rejectionModal) {
            setRejectionModal(false);
          }
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function turnStatusToIn(status) {
    if (typeof status === 'string' || status instanceof String) {
      const parsedStatus = parseInt(status, 10);
      return parsedStatus;
    }
    return status;
  }

  function update(status) {
    setLoading(true);
    const datePartString = dayjs(data?.date).format('YYYY-MM-DD');
    let garageDepartureTime = null;
    let garageArrivalTime = null;
    if (data?.garageDepartureTime) {
      const garageDept = dayjs(data?.garageDepartureTime).format('HH:mm:ss');
      garageDepartureTime = dayjs(`${datePartString}T${garageDept}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    }
    if (data?.garageArrivalTime) {
      const garageArrival = dayjs(data?.garageArrivalTime).format('HH:mm:ss');
      garageArrivalTime = dayjs(`${datePartString}T${garageArrival}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss')
    }

    const formdata = new FormData();
    formdata.append('file', data.fileData);
    formdata.append('modelString', JSON.stringify({
      ...data,
      date: dayjs(data?.date).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss'),
      garageDepartureTime,
      garageArrivalTime,
      routes: _.map(data?.routes, (r) => ({
        ...r,
        status: turnStatusToIn(r.status),
        coordinatedPickupTime: r?.coordinatedPickupTime ? dayjs(`${datePartString}T${dayjs(r?.coordinatedPickupTime).format('HH:mm:ss')}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
        pickupArrivalTime: r?.pickupArrivalTime ? dayjs(`${datePartString}T${dayjs(r?.pickupArrivalTime).format('HH:mm:ss')}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
        destinationArrivalTime: r?.destinationArrivalTime ? dayjs(`${datePartString}T${dayjs(r?.destinationArrivalTime).format('HH:mm:ss')}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null
      }))
    }));
    api.put('sonnell/paratransitservice', formdata)
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setReloadSpecialServices(true);
          setData(localizeParatransitServiceDates(response.data.data));
          if (status) {
            changeStatus(status);
          }
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function create() {
    setLoading(true);
    const datePartString = dayjs(data?.date).format('YYYY-MM-DD');
    let garageDepartureTime = null;
    let garageArrivalTime = null;
    if (data?.garageDepartureTime) {
      const garageDept = dayjs(data?.garageDepartureTime).format('HH:mm:ss');
      garageDepartureTime = dayjs(`${datePartString}T${garageDept}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    }
    if (data?.garageArrivalTime) {
      const garageArrival = dayjs(data?.garageArrivalTime).format('HH:mm:ss');
      garageArrivalTime = dayjs(`${datePartString}T${garageArrival}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss')
    }
    const formdata = new FormData();
    formdata.append('file', data.fileData);
    formdata.append('modelString', JSON.stringify({
      ...data,
      date: dayjs(data?.date).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss'),
      garageDepartureTime,
      garageArrivalTime,
      routes: _.map(data?.routes, (r) => ({
        ...r,
        status: turnStatusToIn(r.status),
        coordinatedPickupTime: r?.coordinatedPickupTime ? dayjs(`${datePartString}T${dayjs(r?.coordinatedPickupTime).format('HH:mm:ss')}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
        pickupArrivalTime: r?.pickupArrivalTime ? dayjs(`${datePartString}T${dayjs(r?.pickupArrivalTime).format('HH:mm:ss')}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null,
        destinationArrivalTime: r?.destinationArrivalTime ? dayjs(`${datePartString}T${dayjs(r?.destinationArrivalTime).format('HH:mm:ss')}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss') : null
      }))
    }));

    api.post('sonnell/paratransitservice', formdata)
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setReloadSpecialServices(true);
          setData(localizeParatransitServiceDates(response.data.data));
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function submit() {
    if (!valid()) {
      return;
    }
    if (data?.id) {
      update();
    } else {
      create();
    }
  }

  function validRoute() {
    let isValid = true;
    let tempData = {
      ...routeData
    };

    if (routeData?.status !== 0 && !routeData?.coordinatedPickupTime) {
      isValid = false;
      tempData = {
        ...tempData,
        coordinatedPickupTimeError: t('time_is_required')
      }
    } else {
      tempData = {
        ...tempData,
        coordinatedPickupTimeError: ''
      }
    }
    
    if (routeData?.status !== 0 && !routeData?.pickupArrivalTime) {
      isValid = false;
      tempData = {
        ...tempData,
        pickupArrivalTimeError: t('time_is_required')
      }
    } else {
      tempData = {
        ...tempData,
        pickupArrivalTimeError: ''
      }
    }
    if (routeData?.status !== 0 && !routeData?.destinationArrivalTime) {
      isValid = false;
      tempData = {
        ...tempData,
        destinationArrivalTimeError: t('time_is_required')
      }
    } else {
      tempData = {
        ...tempData,
        destinationArrivalTimeError: ''
      }
    }

    // if (routeData?.status !== 0 && routeData?.pickupArrivalTime && routeData?.coordinatedPickupTime) {
    //   const datePartString = dayjs(data?.date).format('YYYY-MM-DD');
    //   const startingHour = dayjs(routeData?.coordinatedPickupTime).format('HH:mm:ss');
    //   const endingHour = dayjs(routeData?.pickupArrivalTime).format('HH:mm:ss');
    //   const startingTime = dayjs(`${datePartString}T${startingHour}`).add(4, 'hour');
    //   const endingTime = dayjs(`${datePartString}T${endingHour}`).add(4, 'hour');
    //   if (endingTime.isBefore(startingTime)) {
    //     isValid = false;
    //     tempData = {
    //       ...tempData,
    //       pickupArrivalTimeError: t('time_too_close_to_coordinated_pickup')
    //     }
    //   } else {
    //     tempData = {
    //       ...tempData,
    //       pickupArrivalTimeError: ''
    //     }
    //   }
    // } 
    
    if (routeData?.status !== 0 && routeData?.pickupArrivalTime && routeData?.destinationArrivalTime) {
      const datePartString = dayjs(data?.date).format('YYYY-MM-DD');
      const startingHour = dayjs(routeData?.pickupArrivalTime).format('HH:mm:ss');
      const endingHour = dayjs(routeData?.destinationArrivalTime).format('HH:mm:ss');
      const startingTime = dayjs(`${datePartString}T${startingHour}`).add(4, 'hour');
      const endingTime = dayjs(`${datePartString}T${endingHour}`).add(4, 'hour');

      if (endingTime.isSame(startingTime) || endingTime.isBefore(startingTime)) {
        isValid = false;
        tempData = {
          ...tempData,
          destinationArrivalTimeError: t('time_too_close_to_pickup')
        }
      } else {
        tempData = {
          ...tempData,
          destinationArrivalTimeError: ''
        }
      }
    } 

    let initMil = null;
    let finalMil = null;
    if (routeData?.status !== 0 && routeData?.initialMileage?.length === 0) {
      isValid = false;
      tempData = {
        ...tempData,
        initialMileageError: t('amount_needs_to_be_greater_than_zero')
      }
    } else {
      const tempNum = parseInt(routeData?.initialMileage, 10);
      if (tempNum === 0) {
        isValid = false;
        tempData = {
          ...tempData,
          initialMileageError: t('amount_needs_to_be_greater_than_zero')
        }
      } else {
        initMil = tempNum;
        tempData = {
          ...tempData,
          initialMileageError: ''
        }
      }
    }

    if (routeData?.status !== 0 && routeData?.finalMileage?.length === 0) {
      isValid = false;
      tempData = {
        ...tempData,
        finalMileageError: t('amount_needs_to_be_greater_than_zero')
      }
    } else {
      const tempNum = parseInt(routeData?.finalMileage, 10);
      if (tempNum === 0) {
        isValid = false;
        tempData = {
          ...tempData,
          finalMileageError: t('amount_needs_to_be_greater_than_zero')
        }
      } else {
        finalMil = tempNum;
        tempData = {
          ...tempData,
          finalMileageError: ''
        }
      }
    }

    if (initMil !== null && finalMil !== null) {
      if (finalMil < initMil) {
        isValid = false;
        tempData = {
          ...tempData,
          finalMileageError: t('final_mileage_is_less_than_initial')
        }
      }
    }

    if (routeData?.clientName?.length === 0) {
      isValid = false;
      tempData = {
        ...tempData,
        clientNameError: t('field_is_required')
      }
    } else {
      tempData = {
        ...tempData,
        clientNameError: ''
      }
    }

    if (routeData?.status !== 0 && routeData?.code?.length === 0) {
      isValid = false;
      tempData = {
        ...tempData,
        codeError: t('field_is_required')
      }
    } else {
      tempData = {
        ...tempData,
        codeError: ''
      }
    }

    if (routeData?.tripOriginAddress?.length === 0) {
      isValid = false;
      tempData = {
        ...tempData,
        tripOriginAddressError: t('field_is_required')
      }
    } else {
      tempData = {
        ...tempData,
        tripOriginAddressError: ''
      }
    }

    if (routeData?.tripDestinationAddress?.length === 0) {
      isValid = false;
      tempData = {
        ...tempData,
        tripDestinationAddressError: t('field_is_required')
      }
    } else {
      tempData = {
        ...tempData,
        tripDestinationAddressError: ''
      }
    }

    if (routeData?.status !== 0 && (routeData?.statusMessage === null || routeData?.statusMessage === undefined || routeData?.statusMessage?.length === 0)) {
      isValid = false;
      tempData = {
        ...tempData,
        statusMessageError: t('reason_required_for_missed_trip')
      }
    } else {
      tempData = {
        ...tempData,
        statusMessageError: ''
      }
    }

    setRouteData(tempData);
    return isValid;
  }

  function getServiceRoutes() {
    setLoadingRoute(true);
    api.get(`sonnell/paratransitservice/route?serviceId=${data?.id}`)
    .then((response) => {
      setLoadingRoute(false);
      if (response.data.success) {
        setData({
          ...data,
          routes: _.map(response.data.data, (r) => localizeParatransitRouteDates(r))
        })
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    }).catch((error) => {
      setLoadingRoute(false);
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    });
  }

  function updateRoute() {
    const datePartString = dayjs(data?.date).format('YYYY-MM-DD');
    let coordinatedPickupTime = null;
    let pickupArrivalTime = null;
    let destinationArrivalTime = null;
    if (routeData?.coordinatedPickupTime) {
      const coordPick = dayjs(routeData?.coordinatedPickupTime).format('HH:mm:ss');
      coordinatedPickupTime = dayjs(`${datePartString}T${coordPick}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    }
    if (routeData?.pickupArrivalTime) {
      const pickArrival = dayjs(routeData?.pickupArrivalTime).format('HH:mm:ss');
      pickupArrivalTime = dayjs(`${datePartString}T${pickArrival}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    }
    if (routeData?.destinationArrivalTime) {
      const destArrival = dayjs(routeData?.destinationArrivalTime).format('HH:mm:ss');
      destinationArrivalTime = dayjs(`${datePartString}T${destArrival}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    }
    
    setLoadingRoute(true);
    api.put('sonnell/paratransitservice/route', {
      ...routeData,
      status: turnStatusToIn(routeData.status),
      coordinatedPickupTime,
      pickupArrivalTime,
      destinationArrivalTime
    })
    .then((response) => {
      setLoadingRoute(false);
      if (response.data.success) {
        getServiceRoutes();
        setSelectedRoute(null);
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    }).catch((error) => {
      setLoadingRoute(false);
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    });
  }

  function createRoute() {
    const datePartString = dayjs(data?.date).format('YYYY-MM-DD');
    let coordinatedPickupTime = null;
    let pickupArrivalTime = null;
    let destinationArrivalTime = null;
    if (routeData?.coordinatedPickupTime) {
      const coordPick = dayjs(routeData?.coordinatedPickupTime).format('HH:mm:ss');
      coordinatedPickupTime = dayjs(`${datePartString}T${coordPick}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    }
    if (routeData?.pickupArrivalTime) {
      const pickArrival = dayjs(routeData?.pickupArrivalTime).format('HH:mm:ss');
      pickupArrivalTime = dayjs(`${datePartString}T${pickArrival}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    }
    if (routeData?.destinationArrivalTime) {
      const destArrival = dayjs(routeData?.destinationArrivalTime).format('HH:mm:ss');
      destinationArrivalTime = dayjs(`${datePartString}T${destArrival}`).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    }
    setLoadingRoute(true);
    api.post('sonnell/paratransitservice/route', {
      ...routeData,
      tripNumber: data?.routes?.length + 1,
      sonnellParatransitServiceId: data?.id,
      status: turnStatusToIn(routeData.status),
      coordinatedPickupTime,
      pickupArrivalTime,
      destinationArrivalTime
    })
    .then((response) => {
      setLoadingRoute(false);
      if (response.data.success) {
        getServiceRoutes();
        setSelectedRoute(null);
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    }).catch((error) => {
      setLoadingRoute(false);
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    });
  }

  function removeRoute(route) {
    if (route?.id) {
      setLoadingRoute(true);
      api.delete(`sonnell/paratransitservice/route?routeId=${route.id}`)
      .then((response) => {
        setLoadingRoute(false);
        if (response.data.success) {
          getServiceRoutes();
          setSelectedRoute(null);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      }).catch((error) => {
        setLoadingRoute(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
    } else {
      const tempRoutes = _.filter(data?.routes, (r) => r.tempId !== route.tempId);
      setData({
        ...data,
        routes: calculateTripNumber(tempRoutes)
      });
      setSelectedRoute(null);
    }
  }

  function submitRoute() {
    if (validRoute()) {
      if (routeData?.id) {
        updateRoute();
      } else if (!routeData?.id && routeData?.tempId) {
        const tempRoutes = [...data?.routes];
        for (let i = 0; i < tempRoutes.length; i++) {
          if (tempRoutes[i].tempId === routeData?.tempId) {
            tempRoutes[i].clientName = routeData.clientName;
            tempRoutes[i].code = routeData.code;
            tempRoutes[i].tripOriginAddress = routeData.tripOriginAddress;
            tempRoutes[i].tripDestinationAddress = routeData.tripDestinationAddress;
            tempRoutes[i].coordinatedPickupTime = routeData.coordinatedPickupTime;
            tempRoutes[i].pickupArrivalTime = routeData.pickupArrivalTime;
            tempRoutes[i].destinationArrivalTime = routeData.destinationArrivalTime;
            tempRoutes[i].initialMileage = routeData.initialMileage;
            tempRoutes[i].finalMileage = routeData.finalMileage;
          }
        }
        setData({
          ...data,
          routes: tempRoutes
        });
      } else if (data?.id) {
        createRoute();
      } else {
        const tempData = {
          ...data,
          routes: calculateTripNumber([
            ...data.routes,
            {
              ...routeData,
              tempId: uuidv4()
            }
          ])
        };
        setData(tempData);
        setSelectedRoute(null);
      }
    }
  }

  function getLogs() {
    setLoadingLogs(true);
    api.get(`sonnell/paratransitservice/logs?serviceId=${data?.id}`)
      .then((response) => {
        setLoadingLogs(false);
        if (response.data.success) {
          setLogs(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
    })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  useEffect(() => {
    setData(service);
    setLoading(false);
    setSelectedRoute(null);
  }, [service]);

  useEffect(() => {
    if (selectedRoute) {
      setRouteData(selectedRoute);
    }
  }, [selectedRoute]);

  useEffect(() => {
    setRejectionMessage('');
  }, [rejectionModal]);

  useEffect(() => {
    setIsReadOnly(data?.status === 4 || data?.status === 2);
  }, [data]);

  useEffect(() => {
    setLogs([]);
    if (viewLogs) {
      getLogs();
    }
  }, [viewLogs])

  return (
    <div>
      <Modal
        width="85%"
        open={service}
        title={(
          <Tag className={data?.id ? 'text-lg' : 'hidden'} color={paratransitServiceStatusToColor(data?.status)}>{data?.controlNumber}: {t(paratransitServiceStatusToString(data?.status))}</Tag>
        )}
        footer={[
          <Button
            className={`${data?.id ? '' : 'hidden'} bg-primaryColor px-2 mx-1`}
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            text={t('logs')}
            onClick={() => setViewLogs(true)}
          />,
          <PopconfirmButton
            title={t('are_you_sure_change_status')}
            onConfirm={() => update(1)}
            okText={t('yes')}
            cancelText={t('no')}
            className={`${data?.id && data?.status === 0 ? '' : 'hidden'} bg-primaryColor px-2 mx-1`}
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            text={t('submit_to_review')}
            loading={loading}
            disabled={data?.status === 0 && (data?.routes?.length === 0 || data?.routes === null || data?.routes === undefined)}
          />,
          // <PopconfirmButton
          //   title={t('are_you_sure_change_status')}
          //   onConfirm={() => update(3)}
          //   okText={t('yes')}
          //   cancelText={t('no')}
          //   className={`${userPrivilages?.hasOperatorManagerPrivilages && data?.status === 1 ? '' : 'hidden'} bg-primaryColor px-2 mx-1`}
          //   textColor="text-primaryTextColor"
          //   loaderColor="bg-primaryTextColor"
          //   borderColor="border-primaryBorderColor"
          //   text={t('pre_approve')}
          //   loading={loading}
          // />,
          <PopconfirmButton
            title={t('are_you_sure_change_status')}
            onConfirm={() => setRejectionModal(true)}
            okText={t('yes')}
            cancelText={t('no')}
            className={`${userPrivilages?.hasOperatorManagerPrivilages && (data?.status === 1 || data?.status === 3) ? '' : 'hidden'} bg-rose-500 hover:bg-rose-600 px-2 mx-1`}
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="bg-rose-500"
            text={t('reject')}
            loading={loading}
          />,
          <PopconfirmButton
            title={t('are_you_sure_change_status')}
            onConfirm={() => update(4)}
            okText={t('yes')}
            cancelText={t('no')}
            className={`${userPrivilages?.hasOperatorManagerPrivilages && data?.status === 1 ? '' : 'hidden'} bg-primaryColor px-2 mx-1`}
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            text={t('approve')}
            loading={loading}
            disabled={data?.status === 1 && (data?.routes?.length === 0 || data?.routes === null || data?.routes === undefined)}
          />,
          <Button
            className="bg-primaryColor px-2 mx-1"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            text={data?.id ? t('update') : t('submit')}
            disabled={data?.status === 2}
            loading={loading}
            onClick={() => submit()}
          />,
        ]}
        onCancel={onClose}
      >
        <Spin spinning={loading}>
          <div>
            <Divider orientation="left">{t('service_info')}</Divider>
            <div className="gap-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
              <AntdDatePicker
                readOnly={isReadOnly}
                className="col-span-1 md:col-span-2 lg:col-span-1"
                value={dayjs(data?.date)}
                error={data?.dateError}
                onChange={(obj) => setData({
                  ...data,
                  date: obj.format('MMM DD, YYYY')
                })}
                format="MMM DD, YYYY"
                label={t('date')}
              />
              <AntdInput
                readOnly={isReadOnly}
                label={t('conductor')}
                value={data?.driverName}
                error={data?.driverNameError}
                onChange={(obj) => setData({
                  ...data,
                  driverName: obj.target.value
                })}
                className="w-full col-span-1"
              />
              <AntdInput
                readOnly={isReadOnly}
                label={t('unit_number')}
                value={data?.unitBusNumber}
                error={data?.unitBusNumberError}
                onChange={(obj) => setData({
                  ...data,
                  unitBusNumber: obj.target.value
                })}
                className="w-full col-span-1"
              />
              <AntdInput
                readOnly={isReadOnly}
                label={t('supervisor')}
                value={data?.supervisorName}
                error={data?.supervisorNameError}
                onChange={(obj) => setData({
                  ...data,
                  supervisorName: obj.target.value
                })}
                className="w-full col-span-1"
              />
              <AntdInputNumber
                label={t('passengers')}
                value={data?.passengers}
                error={data?.passengersError}
                step={1}
                formatter={(value) => value ? thousandAllowZero(value) : ''}
                parser={(value) => value ? value.replace(/\$\s?|(,*)/g, '') : ''}
                onChange={(obj) => setData({
                  ...data,
                  passengers: obj
                })}
                className="w-full col-span-1"
              />
              <AntdTimePicker
                className="col-span-1"
                value={data?.garageDepartureTime ? dayjs(data?.garageDepartureTime) : null}
                error={data?.garageDepartureTimeError}
                onChange={(obj) => setData({
                  ...data,
                  garageDepartureTime: obj.format('MMM DD, YYYY hh:mm A')
                })}
                format="hh:mm A"
                label={t('garage_departure_time')}
              />
              <AntdTimePicker
                className="col-span-1"
                value={data?.garageArrivalTime ? dayjs(data?.garageArrivalTime) : null}
                error={data?.garageArrivalTimeError}
                onChange={(obj) => setData({
                  ...data,
                  garageArrivalTime: obj.format('MMM DD, YYYY hh:mm A')
                })}
                format="hh:mm A"
                label={t('garage_arrival_time')}
              />
              <AntdInputNumber
                label={t('initial_mileage')}
                value={data?.initialMileage}
                error={data?.initialMileageError}
                step={1}
                formatter={(value) => value ? thousandAllowZero(value) : ''}
                parser={(value) => value ? value.replace(/\$\s?|(,*)/g, '') : ''}
                onChange={(obj) => setData({
                  ...data,
                  initialMileage: obj
                })}
                className="w-full col-span-1 lg:col-start-1"
              />
              <AntdInputNumber
                label={t('final_mileage')}
                value={data?.finalMileage}
                error={data?.finalMileageError}
                step={1}
                formatter={(value) => value ? thousandAllowZero(value) : ''}
                parser={(value) => value ? value.replace(/\$\s?|(,*)/g, '') : ''}
                onChange={(obj) => setData({
                  ...data,
                  finalMileage: obj
                })}
                className="w-full col-span-1"
              />
            </div>
            <Divider orientation="left">{t('routes')}</Divider>
            <DndContext
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={({ active, over }) => {
                const temp = JSON.parse(JSON.stringify(data));
                if (active.id !== over?.id) {
                  setData((prevState) => {
                    const activeIndex = prevState.routes.findIndex((record) => record.tempId === active?.id);
                    const overIndex = prevState.routes.findIndex((record) => record.tempId === over?.id);
                    const newData = arrayMove(prevState.routes, activeIndex, overIndex);
                    temp.routes = calculateTripNumber(JSON.parse(JSON.stringify(newData)));
                    return temp;
                  });
                }
              }}
            >
              <SortableContext items={_.map(data?.routes, (i) => i.tempId)} strategy={verticalListSortingStrategy}>
                <Table
                  bordered
                  dataSource={data?.routes}
                  rowKey="tempId"
                  className="mt-2"
                  locale={{
                    emptyText: t('no_data')
                  }}
                  title={() => (
                    <div className="flex">
                      <Button
                        className="bg-primaryColor px-2 ml-auto"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="border-primaryBorderColor"
                        text={t('add_new')}
                        disabled={loading || data?.status !== 0 || isReadOnly}
                        onClick={() => setSelectedRoute(defaultRoute)}
                      />
                    </div>
                  )}
                  pagination={false}
                  components={{
                    body: {
                      row: DraggableBodyRow,
                    },
                  }}
                  columns={[
                    {
                      title: '',
                      dataIndex: 'sort',
                      width: 30,
                      key: 'sort',
                      className: `drag-visible ${isReadOnly ? 'hidden' : ''}`,
                      render: () => <DragHandle />,
                    },
                    {
                      title: '',
                      align: 'center',
                      className: 'text-sm lg:hidden',
                      render: (row) => (
                        <div className="grid gap-2 grid-cols-6">
                            <div className="text-left col-span-6">
                              {row.statusMessage?.length > 0 && (
                                <Popover content={row.statusMessage}>
                                  <Tag
                                    color={paratransitServiceRouteStatusToColor(row.status)}
                                  >
                                    {t(paratransitServiceRouteStatusToString(row.status))}
                                  </Tag>
                                </Popover>
                              )}
                              {!(row.statusMessage?.length > 0) && (
                                <Tag
                                  color={paratransitServiceRouteStatusToColor(row.status)}
                                >
                                  {t(paratransitServiceRouteStatusToString(row.status))}
                                </Tag>
                              )}
                            </div>
                            <div className="text-center col-span-6">
                              <p>{t('passenger_name')}</p>
                              <p className="font-bold">{row.clientName}</p>
                            </div>
                            <div className="text-center col-span-6">
                              <p>{t('code')}</p>
                              <p className="font-bold">{row.code}</p>
                            </div>
                            <div className="text-center col-span-6">
                              <p>{t('origin_address')}</p>
                              <p className="font-bold">{row.tripOriginAddress}</p>
                            </div>
                            <div className="text-center col-span-6">
                              <p>{t('destination_address')}</p>
                              <p className="font-bold">{row.tripDestinationAddress}</p>
                            </div>
                            <div className="text-left col-span-3">
                              <p>{t('coordinated_pickup_time')}</p>
                              <p className="font-bold">{dayjs(row.coordinatedPickupTime).format('hh:mm A')}</p>
                            </div>
                            <div className="text-right col-span-3">
                              <p>{t('pickup_arrival_time')}</p>
                              <p className="font-bold">{row.pickupArrivalTime ? dayjs(row.pickupArrivalTime).format('hh:mm A') : '-'}</p>
                            </div>
                            <div className="text-center col-span-6">
                              <p>{t('destination_arrival_time')}</p>
                              <p className="font-bold">{row.destinationArrivalTime ? dayjs(row.destinationArrivalTime).format('hh:mm A') : '-'}</p>
                            </div>
                            <div className="text-left col-span-3">
                              <p>{t('initial_mileage')}</p>
                              <p className="font-bold">{thousand(row.initialMileage)}</p>
                            </div>
                            <div className="text-right col-span-3">
                              <p>{t('final_mileage')}</p>
                              <p className="font-bold">{thousand(row.finalMileage)}</p>
                            </div>
                            <div className="col-start-2 col-span-4 md:col-start-3 md:col-span-2">
                              <Button
                                className="bg-primaryColor px-2 mx-1 w-full"
                                textColor="text-primaryTextColor"
                                loaderColor="bg-primaryTextColor"
                                borderColor="border-primaryBorderColor"
                                text={t('edit')}
                                disabled={data?.status === 2}
                                loading={loading}
                                onClick={() => setSelectedRoute(row)}
                              />
                            </div>
                        </div>
                      )
                    },
                    {
                      title: t('passenger_name'),
                      align: 'center',
                      className: 'text-sm hidden lg:table-cell',
                      render: (row) => (
                        <span>{row.clientName}</span>
                      ),
                    },
                    {
                      title: t('code'),
                      align: 'center',
                      className: 'text-sm hidden lg:table-cell',
                      render: (row) => (
                        <span>{row.code}</span>
                      ),
                    },
                    {
                      title: t('origin_address'),
                      align: 'center',
                      className: 'text-sm hidden lg:table-cell',
                      render: (row) => (
                        <span>{row.tripOriginAddress}</span>
                      ),
                    },
                    {
                      title: t('coordinated_pickup_time'),
                      align: 'center',
                      className: 'text-sm hidden lg:table-cell',
                      render: (row) => (
                        <span>{dayjs(row.coordinatedPickupTime).format('hh:mm A')}</span>
                      ),
                    },
                    {
                      title: t('destination_address'),
                      align: 'center',
                      className: 'text-sm hidden lg:table-cell',
                      render: (row) => (
                        <span>{row.tripDestinationAddress}</span>
                      ),
                    },
                    {
                      title: t('pickup_arrival_time'),
                      align: 'center',
                      className: 'text-sm hidden lg:table-cell',
                      render: (row) => (
                        <span>{row.pickupArrivalTime ? dayjs(row.pickupArrivalTime).format('hh:mm A') : '-'}</span>
                      ),
                    },
                    {
                      title: t('destination_arrival_time'),
                      align: 'center',
                      className: 'text-sm hidden lg:table-cell',
                      render: (row) => (
                        <span>{row.destinationArrivalTime ? dayjs(row.destinationArrivalTime).format('hh:mm A') : '-'}</span>
                      ),
                    },
                    {
                      title: t('initial_mileage'),
                      align: 'center',
                      className: 'text-sm hidden lg:table-cell',
                      render: (row) => (
                        <span>{thousand(row.initialMileage)}</span>
                      ),
                    },
                    {
                      title: t('final_mileage'),
                      align: 'center',
                      className: 'text-sm hidden lg:table-cell',
                      render: (row) => (
                        <span>{thousand(row.finalMileage)}</span>
                      ),
                    },
                    {
                      title: t('status'),
                      align: 'center',
                      className: 'text-sm hidden lg:table-cell',
                      render: (row) => (
                        <>
                          {row.statusMessage?.length > 0 && (
                            <Popover content={row.statusMessage}>
                              <Tag
                                color={paratransitServiceRouteStatusToColor(row.status)}
                              >
                                {t(paratransitServiceRouteStatusToString(row.status))}
                              </Tag>
                            </Popover>
                          )}
                          {!(row.statusMessage?.length > 0) && (
                            <Tag
                              color={paratransitServiceRouteStatusToColor(row.status)}
                            >
                              {t(paratransitServiceRouteStatusToString(row.status))}
                            </Tag>
                          )}
                        </>
                      )
                    },
                    {
                      title: t('actions'),
                      align: 'center',
                      className: 'text-sm hidden lg:table-cell',
                      render: (row) => (
                        <Button
                          className="bg-primaryColor px-2 mx-1"
                          textColor="text-primaryTextColor"
                          loaderColor="bg-primaryTextColor"
                          borderColor="border-primaryBorderColor"
                          text={t('edit')}
                          loading={loading}
                          disabled={data?.status === 2}
                          onClick={() => setSelectedRoute(row)}
                        />
                      ),
                    },
                  ]}
                />
              </SortableContext>
            </DndContext>

            <Divider orientation="left" className="mt-6">{t('optional_copy_of_document')}</Divider>
            {!isReadOnly && (
              <Dragger className="mt-4 h-42 md:h-28" disabled={loading} {...draggerProps}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {t('click_or_drag_content')}
                </p>
              </Dragger>
            )}
            {isReadOnly && !data?.fileUrl && (
              <p className="italic text-gray-500">N/A</p>
            )}
            {data?.fileUrl?.length > 0 && (
              <a href={data?.fileUrl} target="_blank" rel="noreferrer">
                <Button
                  className="bg-primaryColor mb-4 px-2 mt-2"
                  textColor="text-primaryTextColor"
                  loaderColor="bg-primaryTextColor"
                  borderColor="border-primaryBorderColor"
                  text={`${t('preview_file')}: ${data?.fileName}`}
                  loading={loading}
                />
              </a>
            )}

            {data?.rejectionMessage && (
              <>
                <Divider orientation="left">{t('rejection_message')}</Divider>
                <p className="italic text-gray-500">{data?.rejectionMessage}</p>
              </>
            )}
          </div>
        </Spin>
      </Modal>
      <Modal
        width="75%"
        open={selectedRoute}
        footer={[
          <PopconfirmButton
            title={t('are_you_sure_remove_route')}
            okText={t('yes')}
            cancelText={t('no')}
            onConfirm={() => removeRoute(routeData)}
            className={`${(routeData?.id || routeData?.tempId) ? '' : 'hidden'} bg-rose-500 hover:bg-rose-600 px-2 mx-1`}
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-rose-500 border:bg-rose-600"
            disabled={data?.status !== 0}
            text={t('remove')}
            loading={loading}
          />,
          <Button
            className="bg-primaryColor px-2 mx-1"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            text={routeData?.id ? t('update') : t('submit')}
            loading={loading}
            disabled={data?.status === 2}
            onClick={() => submitRoute()}
          />
        ]}
        onCancel={() => setSelectedRoute(null)}
      >
        <Spin spinning={loadingRoute}>
          <div>
            <Divider orientation="left">{routeData?.id || routeData?.tempId ? t('update_route') : t('new_route')}</Divider>
            <div className="grid gap-2 grid-cols-1 lg:grid-cols-6">
              <AntdInput
                label={t('passenger_name')}
                required
                readOnly={data?.status !== 0}
                value={routeData?.clientName}
                error={routeData?.clientNameError}
                onChange={(obj) => setRouteData({
                  ...routeData,
                  clientName: obj.target.value
                })}
                className="w-full col-span-1 md:col-span-4"
              />
              <AntdInput
                label={t('code')}
                value={routeData?.code}
                error={routeData?.codeError}
                onChange={(obj) => setRouteData({
                  ...routeData,
                  code: obj.target.value
                })}
                className="w-full col-span-1 md:col-span-2"
              />
              <AntdInput
                label={t('origin_address')}
                required
                readOnly={data?.status !== 0}
                value={routeData?.tripOriginAddress}
                error={routeData?.tripOriginAddressError}
                onChange={(obj) => setRouteData({
                  ...routeData,
                  tripOriginAddress: obj.target.value
                })}
                className="w-full col-span-1 md:col-span-6"
              />
              <AntdInput
                label={t('destination_address')}
                required
                readOnly={data?.status !== 0}
                value={routeData?.tripDestinationAddress}
                error={routeData?.tripDestinationAddressError}
                onChange={(obj) => setRouteData({
                  ...routeData,
                  tripDestinationAddress: obj.target.value
                })}
                className="w-full col-span-1 md:col-span-6"
              />
              <AntdTimePicker
                className="col-span-1 md:col-span-2"
                value={dayjs(routeData?.coordinatedPickupTime)}
                error={routeData?.coordinatedPickupTimeError}
                onChange={(obj) => setRouteData({
                  ...routeData,
                  coordinatedPickupTime: obj.format('MMM DD, YYYY hh:mm A')
                })}
                format="hh:mm A"
                readOnly={data?.status !== 0}
                label={t('coordinated_pickup_time')}
              />
              <AntdTimePicker
                className="col-span-1 md:col-span-2"
                value={routeData?.pickupArrivalTime ? dayjs(routeData?.pickupArrivalTime) : null}
                error={routeData?.pickupArrivalTimeError}
                onChange={(obj) => setRouteData({
                  ...routeData,
                  pickupArrivalTime: obj.format('MMM DD, YYYY hh:mm A')
                })}
                format="hh:mm A"
                label={t('pickup_arrival_time')}
              />
              <AntdTimePicker
                className="col-span-1 md:col-span-2"
                value={routeData?.destinationArrivalTime ? dayjs(routeData?.destinationArrivalTime) : null}
                error={routeData?.destinationArrivalTimeError}
                onChange={(obj) => setRouteData({
                  ...routeData,
                  destinationArrivalTime: obj.format('MMM DD, YYYY hh:mm A')
                })}
                format="hh:mm A"
                label={t('destination_arrival_time')}
              />
              <AntdInputNumber
                label={t('initial_mileage')}
                value={routeData?.initialMileage}
                error={routeData?.initialMileageError}
                step={1}
                formatter={(value) => value ? thousandAllowZero(value) : ''}
                parser={(value) => value ? value.replace(/\$\s?|(,*)/g, '') : ''}
                onChange={(obj) => setRouteData({
                  ...routeData,
                  initialMileage: obj
                })}
                className="w-full col-span-1 md:col-span-2"
              />
              <AntdInputNumber
                label={t('final_mileage')}
                value={routeData?.finalMileage}
                error={routeData?.finalMileageError}
                step={1}
                formatter={(value) => value ? thousandAllowZero(value) : ''}
                parser={(value) => value ? value.replace(/\$\s?|(,*)/g, '') : ''}
                onChange={(obj) => setRouteData({
                  ...routeData,
                  finalMileage: obj
                })}
                className="w-full col-span-1 md:col-span-2"
              />
              <Select
                label={t('status')}
                className="w-full col-span-1 md:col-span-2"
                value={routeData?.status}
                options={_.map(paratransitServiceRouteStatus, (s) => ({
                  key: s.id,
                  value: s.id,
                  text: t(s.name)
                }))}
                onChange={(obj) => setRouteData({
                  ...routeData,
                  status: obj.target.value
                })}
              />
              <AntdInput
                label={t('reason')}
                required
                error={routeData?.statusMessageError}
                value={routeData?.statusMessage}
                onChange={(obj) => setRouteData({
                  ...routeData,
                  statusMessage: obj.target.value
                })}
                className={`${routeData?.status <= 1 ? 'hidden' : ''} w-full col-span-1 md:col-span-6`}
              />
            </div>
          </div>
        </Spin>
      </Modal>
      <Modal
        open={rejectionModal}
        onCancel={() => setRejectionModal(false)}
        footer={[
          <Button
            className="bg-primaryColor px-2 mx-1"
            textColor="text-primaryTextColor"
            loaderColor="bg-primaryTextColor"
            borderColor="border-primaryBorderColor"
            text={t('submit')}
            loading={loading}
            onClick={() => update(2)}
          />
        ]}
      >
        <Divider orientation="left">{t('rejection_message')}</Divider>
        <AntdInput
          label={t('optional_message')}
          value={rejectionMessage}
          disabled={loading}
          onChange={(obj) => setRejectionMessage(obj.target.value)}
          className="w-full"
        />
      </Modal>
      <Modal
        open={viewLogs}
        onCancel={() => setViewLogs(false)}
        footer={[]}
      >
        <Divider orientation="left">{t('logs')}</Divider>
        <Table
          bordered
          loading={loadingLogs}
          dataSource={logs}
          rowKey="id"
          locale={{
            emptyText: t('no_data')
          }}
          columns={[
            {
              title: '',
              align: 'center',
              className: 'text-sm lg:hidden',
              render: (row) => (
                <div>
                  <p><strong>{t('created_on')}</strong>: {toLocalTime(row.createdAt).format('MMM DD, YYYY hh:mm A')}</p>
                  <p className="mt-1">{row.message}</p>
                </div>
              ),
            },
            {
              title: t('created_on'),
              align: 'center',
              className: 'text-sm hidden lg:table-cell',
              render: (row) => (
                <span>{toLocalTime(row.createdAt).format('MMM DD, YYYY hh:mm A')}</span>
              ),
            },
            {
              title: t('reason'),
              align: 'left',
              className: 'text-sm hidden lg:table-cell',
              render: (row) => (
                <span>{row.message}</span>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
}

export default SelectedParatransitServicesModal;
